import { useParams, Navigate, Link } from 'react-router-dom'
import { getJourneyBySlug } from '@/data/journeys'
import type { TimelineStep, JourneyFeature } from '@/data/journeys'
import { Button, SignupCta } from '@/components/ui'
import { SEO } from '@/components/seo'

// Icon component for timeline and features
function Icon({
  name,
  color,
  className,
}: {
  name: string
  color?: string
  className?: string
}) {
  const colorClasses: Record<string, string> = {
    teal: 'bg-teal-50 text-accent-teal',
    violet: 'bg-violet-50 text-violet-600',
    amber: 'bg-amber-50 text-amber-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    rose: 'bg-rose-50 text-rose-600',
    pink: 'bg-pink-50 text-pink-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    sky: 'bg-sky-50 text-sky-600',
  }

  const icons: Record<string, React.ReactNode> = {
    video: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
    document: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    clipboard: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
    search: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
    sparkle: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
      </svg>
    ),
    mic: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
      </svg>
    ),
    shield: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
    chart: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
    users: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    clock: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    bolt: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    check: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  }

  return (
    <div
      className={`${color ? colorClasses[color] || colorClasses.teal : ''} ${className || ''}`}
    >
      {icons[name] || icons.document}
    </div>
  )
}

// Timeline step component
function TimelineStepCard({ step, index }: { step: TimelineStep; index: number }) {
  return (
    <div className="relative pl-[88px] pb-16 last:pb-0">
      {/* Marker */}
      <div className="absolute left-4 top-0 w-[34px] h-[34px] bg-gradient-to-br from-accent-teal to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-[0_0_0_4px_#ffffff,0_0_12px_rgba(15,118,110,0.2)]">
        {index + 1}
      </div>

      {/* Time */}
      <div className="text-[13px] font-semibold text-accent-teal tracking-[0.05em] mb-2">
        {step.time}
      </div>

      {/* Card */}
      <div className="bg-white border border-slate-200 rounded-2xl py-7 px-8 shadow-card transition-all duration-400 hover:border-slate-300 hover:translate-x-2 hover:shadow-lg">
        <div className="flex items-center gap-4 mb-4">
          <Icon
            name={step.icon}
            color={step.iconColor}
            className="w-12 h-12 rounded-xl flex items-center justify-center [&>svg]:w-6 [&>svg]:h-6"
          />
          <div>
            <h3 className="font-serif text-[22px] font-medium text-slate-900 mb-1">{step.title}</h3>
            <p className="text-[13px] text-slate-400">{step.meta}</p>
          </div>
        </div>
        <p className="text-[15px] text-slate-600 leading-[1.7] mb-5">{step.body}</p>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <p className="text-[10px] font-semibold text-accent-teal tracking-[0.1em] uppercase mb-2">
            {step.previewLabel}
          </p>
          <p className="text-[13px] text-slate-600 leading-[1.6]">{step.previewText}</p>
        </div>
      </div>
    </div>
  )
}

// Feature card component
function FeatureCard({ feature }: { feature: JourneyFeature }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl py-10 px-8 shadow-card transition-all duration-400 hover:-translate-y-1 hover:border-slate-300 hover:shadow-lg relative overflow-hidden group">
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-accent-teal to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
      <Icon
        name={feature.icon}
        color="teal"
        className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 [&>svg]:w-8 [&>svg]:h-8"
      />
      <h3 className="font-serif text-[22px] font-medium tracking-[-0.01em] text-slate-900 mb-3">
        {feature.title}
      </h3>
      <p className="text-[15px] text-slate-600 leading-[1.7]">{feature.body}</p>
    </div>
  )
}

export default function JourneyPage() {
  const { slug } = useParams<{ slug: string }>()
  const journey = slug ? getJourneyBySlug(slug) : undefined

  if (!journey) {
    return <Navigate to="/" replace />
  }

  return (
    <>
      <SEO
        title={journey.seo.title}
        description={journey.seo.description}
        keywords={journey.seo.keywords}
        url={`/case-studies/${journey.slug}`}
      />

      {/* Hero */}
      <section className="pt-24 pb-16 px-6 max-w-[1200px] mx-auto text-center">
        <p className="text-[14px] font-semibold text-accent-teal tracking-[0.1em] uppercase mb-6">
          {journey.hero.eyebrow}
        </p>
        <h1 className="font-serif text-[clamp(42px,6vw,64px)] font-normal leading-[1.15] tracking-[-0.02em] text-slate-900 mb-6 max-w-[900px] mx-auto">
          {journey.hero.title}
          <br />
          <span className="text-accent-teal">{journey.hero.titleAccent}</span>
        </h1>
        <p className="text-[19px] text-slate-600 leading-[1.7] max-w-[600px] mx-auto mb-10">
          {journey.hero.subtitle}
        </p>
        <div className="flex gap-4 justify-center mb-4">
          <SignupCta location="journey_cta">
            Start Free Trial
            <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </SignupCta>
          <Button href="/pricing" variant="secondary" size="lg">
            View Pricing
          </Button>
        </div>
        <p className="text-sm text-slate-400">
          {journey.hero.microcopy}
        </p>
      </section>

      {/* Journey Timeline */}
      <section className="py-20 px-6">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-20">
            <p className="text-xs font-semibold text-accent-teal tracking-[0.15em] uppercase mb-4">
              {journey.journeySection.label}
            </p>
            <h2 className="font-serif text-[clamp(32px,4vw,42px)] font-normal tracking-[-0.02em] text-slate-900 mb-4">
              {journey.journeySection.title}{' '}
              <span className="text-accent-teal">{journey.journeySection.titleAccent}</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-[500px] mx-auto">
              {journey.journeySection.subtitle}
            </p>
          </div>

          {/* Timeline */}
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-accent-teal to-teal-200" />
            {journey.timeline.map((step, index) => (
              <TimelineStepCard key={index} step={step} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Result */}
      <section className="py-10 px-6 pb-20">
        <div className="max-w-[900px] mx-auto bg-teal-50 border border-teal-200 rounded-3xl p-12 text-center">
          <div className="w-[72px] h-[72px] bg-gradient-to-br from-accent-teal to-emerald-500 rounded-[20px] flex items-center justify-center mx-auto mb-6 text-white">
            <svg className="w-9 h-9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="font-serif text-[32px] font-normal text-slate-900 mb-4">
            {journey.result.title} <span className="text-accent-teal">{journey.result.titleAccent}</span>
          </h2>
          <p className="text-lg text-slate-600 leading-[1.7] max-w-[550px] mx-auto mb-8">
            {journey.result.body}
          </p>
          <div className="pt-8 border-t border-teal-200">
            <p className="text-[11px] font-semibold text-slate-400 tracking-[0.15em] uppercase mb-6">
              Illustrative scenario
            </p>
            <div className="flex justify-center gap-12">
              {journey.result.stats.map((stat, index) => (
                <div key={index}>
                  <div className="font-serif text-[42px] font-light text-accent-teal">{stat.value}</div>
                  <div className="text-sm text-slate-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-20">
            <p className="text-xs font-semibold text-accent-teal tracking-[0.15em] uppercase mb-5">
              {journey.features.label}
            </p>
            <h2 className="font-serif text-[48px] font-normal tracking-[-0.02em] text-slate-900 mb-5">
              {journey.features.title}{' '}
              <span className="text-accent-teal">{journey.features.titleAccent}</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-[600px] mx-auto">
              {journey.features.subtitle}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {journey.features.items.map((feature, index) => (
              <FeatureCard key={index} feature={feature} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 px-6">
        <div className="max-w-[800px] mx-auto bg-white border border-slate-200 rounded-2xl p-10 text-center shadow-card">
          <div className="font-serif text-[80px] text-teal-100 leading-[0.5] mb-6">&ldquo;</div>
          <p className="text-[17px] text-slate-700 leading-[1.8] mb-8">{journey.testimonial.quote}</p>
          <div className="flex items-center justify-center gap-4 pt-6 border-t border-slate-200">
            <div className="w-12 h-12 bg-gradient-to-br from-accent-teal to-emerald-500 rounded-xl flex items-center justify-center text-sm font-semibold text-white">
              {journey.testimonial.initials}
            </div>
            <div className="text-left">
              <div className="text-[15px] font-semibold text-slate-900">
                {journey.testimonial.authorName}
              </div>
              <div className="text-[13px] text-slate-600">{journey.testimonial.authorRole}</div>
              {journey.testimonial.authorLocation && (
                <div className="text-xs text-slate-400">{journey.testimonial.authorLocation}</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 pb-20">
        <div className="max-w-[900px] mx-auto bg-white border border-slate-200 rounded-3xl py-20 px-12 text-center shadow-card">
          <h2 className="font-serif text-[44px] font-normal tracking-[-0.02em] text-slate-900 mb-5">
            {journey.cta.title} <span className="text-accent-teal">{journey.cta.titleAccent}</span> next meeting
          </h2>
          <p className="text-lg text-slate-600 leading-[1.7] max-w-[500px] mx-auto mb-10">
            {journey.cta.subtitle}
          </p>
          <div className="flex justify-center gap-4 mb-6">
            <SignupCta location="journey_cta">
              Start Free Trial
              <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </SignupCta>
            <Button href="/pricing" variant="secondary" size="lg">
              View Pricing
            </Button>
          </div>
          <p className="text-sm text-slate-400">{journey.cta.note}</p>
        </div>
      </section>

      {/* Back to Case Studies */}
      <div className="text-center px-6 pb-16">
        <Link
          to="/case-studies"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-600 transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to case studies
        </Link>
      </div>
    </>
  )
}
